import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuthService } from 'src/app/services/auth.service';

/**
 * Step 1 of the forgot-password flow: collect the account email and email a
 * one-time reset code. The backend always reports success regardless of
 * whether the address is registered (anti-enumeration), so this screen
 * can't be used to probe which emails have accounts — matches the mobile
 * app's ForgotPassword screen.
 */
@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss'
})
export class ForgotPasswordComponent {
  emailForm: FormGroup;
  loading = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private auth: AuthService,
  ) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  async submit(): Promise<void> {
    if (!this.emailForm.valid || this.loading) {
      return;
    }

    this.loading = true;
    const email = this.emailForm.value.email;

    try {
      await this.auth.requestPasswordReset(email);
      // A true request failure is caught below — beyond that, the backend
      // always reports success regardless of whether the email is
      // registered, so this is the only outcome that reaches here on purpose.
      this.router.navigate(['/reset-password'], { queryParams: { email } });
    } catch {
      // AuthService/GraphqlService already surface a toast on failure.
    } finally {
      this.loading = false;
    }
  }
}
